import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { Users as UsersIcon, Edit, Trash2, Plus, ChevronLeft, ChevronRight, Download, Upload, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Users({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "user" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 0 });
  
  // Estados para import CSV
  const [importing, setImporting] = useState(false);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados para multi-select
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteSelectedConfirm, setDeleteSelectedConfirm] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin') {
      toast.error("Acesso negado");
      return;
    }
    setSelectedUsers([]); // Limpa seleção ao mudar de página
    fetchUsers();
  }, [user?.role, pagination.page, pagination.page_size]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users", {
        params: {
          page: pagination.page,
          page_size: pagination.page_size
        }
      });
      setUsers(response.data.users);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        total_pages: response.data.pagination.total_pages
      }));
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/users/${userId}/role?role=${newRole}`);
      toast.success("Role atualizado com sucesso!");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar role");
    }
  };

  const handleEditClick = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, email: u.email, role: u.role });
  };

  const handleEditSave = async () => {
    try {
      await axiosInstance.put(
        `/users/${editingUser.id}?name=${encodeURIComponent(editForm.name)}&email=${encodeURIComponent(editForm.email)}&role=${editForm.role}`
      );
      toast.success("Usuário atualizado com sucesso");
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar usuário");
      console.error(error);
    }
  };

  const handleDeleteClick = (u) => {
    setDeleteConfirm(u);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axiosInstance.delete(`/users/${deleteConfirm.id}`);
      toast.success("Usuário excluído com sucesso");
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir usuário");
      console.error(error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/auth/register", {
        name: newUserForm.name,
        email: newUserForm.email,
        password: newUserForm.password,
        role: newUserForm.role
      });
      toast.success("Usuário criado com sucesso!");
      setCreateDialogOpen(false);
      setNewUserForm({ name: "", email: "", password: "", role: "user" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar usuário");
      console.error(error);
    }
  };

  // Exportar usuários para CSV (incluindo senha)
  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      const response = await axiosInstance.get("/users/export/csv", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `usuarios_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Usuários exportados com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar usuários");
      console.error(error);
    }
  };

  // Importar usuários de CSV
  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axiosInstance.post("/users/import/csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar usuários");
      console.error(error);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Baixar modelo CSV
  const handleDownloadTemplate = () => {
    const headers = "Nome,Email,Senha,Role";
    const example = "João Silva,joao@email.com,senha123,user";
    const content = `${headers}\n${example}`;
    
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "modelo_usuarios.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast.success("Modelo baixado!");
  };

  // Deletar todos os usuários
  const handleDeleteAllUsers = async () => {
    try {
      const response = await axiosInstance.delete("/users/all");
      toast.success(response.data.message);
      setDeleteAllConfirm(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao deletar usuários");
      console.error(error);
    }
  };

  // Multi-select functions
  const handleSelectAll = (checked) => {
    // Seleciona apenas os usuários da página atual (exceto o próprio usuário logado)
    const selectableUsers = users.filter(u => u.id !== user?.id);
    if (checked) {
      // Adiciona os da página atual aos já selecionados
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        selectableUsers.forEach(u => newSet.add(u.id));
        return Array.from(newSet);
      });
    } else {
      // Remove apenas os da página atual
      const pageUserIds = selectableUsers.map(u => u.id);
      setSelectedUsers(prev => prev.filter(id => !pageUserIds.includes(id)));
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Verificar se todos da página atual estão selecionados
  const selectableUsersOnPage = users.filter(u => u.id !== user?.id);
  const isAllSelected = selectableUsersOnPage.length > 0 && 
    selectableUsersOnPage.every(u => selectedUsers.includes(u.id));

  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      const response = await axiosInstance.delete("/users/bulk-delete", {
        data: selectedUsers
      });
      toast.success(response.data.message);
      setDeleteSelectedConfirm(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao deletar usuários selecionados");
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Usuários</h1>
          <p className="text-gray-600">Gerencie usuários e permissões do sistema</p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              Lista de Usuários ({pagination.total})
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              {selectedUsers.length > 0 && (
                <Button
                  onClick={() => setDeleteSelectedConfirm(true)}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                  size="sm"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Selecionados ({selectedUsers.length})
                </Button>
              )}
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Modelo CSV
              </Button>
              <Button
                onClick={handleExportCSV}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                size="sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importando..." : "Importar CSV"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Usuário
              </Button>
              <Button
                onClick={() => setDeleteAllConfirm(true)}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Deletar Todos
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum usuário cadastrado ainda.
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Data Cadastro</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedUsers.includes(u.id)}
                          onCheckedChange={(checked) => handleSelectUser(u.id, checked)}
                          disabled={u.id === user?.id}
                          data-testid={`select-user-${u.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <select
                          data-testid={`role-select-${u.id}`}
                          className={`px-3 py-1 rounded-md text-xs font-semibold border-0 cursor-pointer ${
                            u.role === 'master' ? 'bg-slate-900 text-white' :
                            u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                            u.role === 'operador' ? 'bg-orange-100 text-orange-700' : 
                            u.role === 'partner_registration' ? 'bg-green-100 text-green-700' :
                            u.role === 'powerplant_registration' ? 'bg-amber-100 text-amber-800' :
                            'bg-blue-100 text-blue-700'
                          }`}
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        >
                          <option value="user">Usuário</option>
                          <option value="admin">Administrador</option>
                  <option value="master">Master</option>
                          <option value="operador">Operador</option>
                          <option value="partner_registration">Cadastro de Parceiro</option>
                  <option value="powerplant_registration">Cadastro de Usina</option>
                        </select>
                      </TableCell>
                      <TableCell>{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditClick(u)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Editar usuário"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(u)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Excluir usuário"
                            disabled={u.id === user?.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}

            {/* Paginação */}
            {!loading && users.length > 0 && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Mostrar</span>
                  <select
                    value={pagination.page_size}
                    onChange={(e) => setPagination({ ...pagination, page: 1, page_size: Number(e.target.value) })}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-600">por página</span>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Mostrando {users.length === 0 ? 0 : ((pagination.page - 1) * pagination.page_size + 1)} a{" "}
                    {Math.min(pagination.page * pagination.page_size, pagination.total)} de{" "}
                    {pagination.total} usuários
                  </span>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(pagination.total_pages, 5))].map((_, idx) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = idx + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = idx + 1;
                        } else if (pagination.page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + idx;
                        } else {
                          pageNum = pagination.page - 2 + idx;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPagination({ ...pagination, page: pageNum })}
                            className={`h-8 w-8 p-0 ${pagination.page === pageNum ? 'bg-gradient-to-r from-blue-500 to-cyan-600' : ''}`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page === pagination.total_pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Editar Usuário</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="user">Usuário</option>
                    <option value="admin">Administrador</option>
                  <option value="master">Master</option>
                    <option value="operador">Operador</option>
                    <option value="partner_registration">Cadastro de Parceiro</option>
                  <option value="powerplant_registration">Cadastro de Usina</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEditSave}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Criar Novo Usuário</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-name">Nome Completo</Label>
                <Input
                  id="new-name"
                  type="text"
                  placeholder="Nome do usuário"
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-email">Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Senha do usuário"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-role">Role</Label>
                <select
                  id="new-role"
                  value={newUserForm.role}
                  onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                  <option value="master">Master</option>
                  <option value="operador">Operador</option>
                  <option value="partner_registration">Cadastro de Parceiro</option>
                  <option value="powerplant_registration">Cadastro de Usina</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  <strong>Cadastro de Parceiro:</strong> Acesso limitado apenas à página de cadastro de parceiros nível 1.
                  {" "}<strong>Cadastro de Usina:</strong> Acesso limitado apenas à tela de Nova Usina
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    setNewUserForm({ name: "", email: "", password: "", role: "user" });
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                >
                  Criar Usuário
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-red-600">Confirmar Exclusão</h2>
              <p className="text-gray-700 mb-6">
                Tem certeza que deseja excluir o usuário <strong>{deleteConfirm.name}</strong>?
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Users Confirmation Dialog */}
        <Dialog open={deleteAllConfirm} onOpenChange={setDeleteAllConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Deletar Todos os Usuários
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Esta ação irá excluir <strong>TODOS</strong> os usuários do sistema, exceto o usuário logado.
                <br /><br />
                <span className="text-red-600 font-semibold">Esta ação NÃO pode ser desfeita!</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteAllConfirm(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteAllUsers}
                className="bg-red-600 hover:bg-red-700"
              >
                Confirmar Exclusão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Selected Users Confirmation Dialog */}
        <Dialog open={deleteSelectedConfirm} onOpenChange={setDeleteSelectedConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Deletar Usuários Selecionados
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Você está prestes a excluir <strong>{selectedUsers.length}</strong> usuário(s) selecionado(s).
                <br /><br />
                <span className="text-red-600 font-semibold">Esta ação NÃO pode ser desfeita!</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteSelectedConfirm(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteSelected}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-delete-selected-btn"
              >
                Confirmar Exclusão ({selectedUsers.length})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
